/**
 * Expiry status — three-tone traffic light per SPEC §2 US-03.
 *   - 🔴 expired  — already past or today
 *   - 🟡 warning  — within `warningDays` (default 3)
 *   - 🟢 fresh    — beyond warning window or no expiry tracked
 */
export type ExpiryStatus = 'expired' | 'warning' | 'fresh';

export interface ExpiryInfo {
  status: ExpiryStatus;
  daysLeft: number | null;
}

/** Days from start-of-today to start-of-expiry-day (negative = past). */
export function daysUntilExpiry(expiresAt: number | null, now: number = Date.now()): number | null {
  if (expiresAt === null) return null;
  const startOfDay = (ms: number) => {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  return Math.round((startOfDay(expiresAt) - startOfDay(now)) / 86_400_000);
}

export function getExpiryStatus(
  expiresAt: number | null,
  warningDays: number,
  now: number = Date.now()
): ExpiryInfo {
  const daysLeft = daysUntilExpiry(expiresAt, now);
  if (daysLeft === null) return { status: 'fresh', daysLeft: null };
  if (daysLeft <= 0) return { status: 'expired', daysLeft };
  if (daysLeft <= warningDays) return { status: 'warning', daysLeft };
  return { status: 'fresh', daysLeft };
}

/** Aggregate the worst-case status across an item's batches (drives badge colour). */
export function aggregateItemStatus(
  batches: ReadonlyArray<{ quantity: number; expiresAt: number | null }>,
  warningDays: number,
  now: number = Date.now()
): ExpiryInfo {
  const active = batches.filter((b) => b.quantity > 0);
  if (active.length === 0) return { status: 'fresh', daysLeft: null };

  let worst: ExpiryInfo = { status: 'fresh', daysLeft: null };
  const rank = (s: ExpiryStatus) => (s === 'expired' ? 2 : s === 'warning' ? 1 : 0);

  for (const batch of active) {
    const info = getExpiryStatus(batch.expiresAt, warningDays, now);
    if (rank(info.status) > rank(worst.status)) {
      worst = info;
    } else if (
      info.status === worst.status &&
      info.daysLeft !== null &&
      (worst.daysLeft === null || info.daysLeft < worst.daysLeft)
    ) {
      worst = info;
    }
  }
  return worst;
}
